import React, { useState, useEffect, useCallback } from "react";
import api from "./axiosConfig";
import { useNavigate } from "react-router-dom";
import ClockInMap from "./components/ClockInMap";
import "./UserPage.css"; // Import CSS file
import { format } from "date-fns";
import Modal from "./components/Modal";
import { jwtDecode } from "jwt-decode";

function UserPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [location, setLocation] = useState();
  const [isInside, setIsInside] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); 
  const [remarks, setRemarks] = useState("");
  const [clockRecords, setClockRecords] = useState(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [isOvertimeModalOpen, setIsOvertimeModalOpen] = useState(false);
  const [userDropdown, setUserDropdown] = useState([]);

  const token = localStorage.getItem("accessToken");

  const navigate = useNavigate();

  const getClockInRecord = useCallback(async () => {
    const record = await api.get(
      "/clock-record/detail",
      { headers: { Authorization: token } }
    );

    if (record === null){
      return;
    }
    setRemarks(record.data.remarks ?? "");
    setClockRecords(record.data);
  },[token]);

  const fetchUsers = async () => {
    try {
      const userId = jwtDecode(token).id;
      const response = await api.get("user/public/dropdown", {
        headers: { Authorization: token },
      });
      const filtered = response.data.filter(u => u.id !== userId);
      setUserDropdown(filtered);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage("");
      }, 5000);
  
      return () => clearTimeout(timer); 
    }
  }, [message]);

  useEffect(() => {
    const checkPermissionAndGetLocation = async () => {
      try {
        const permissionStatus = await navigator.permissions.query({ name: "geolocation" });

        if (permissionStatus.state === "denied") {
          setPermissionDenied(true);
          return;
        }

        navigator.geolocation.getCurrentPosition(
          (position) => {
            const newLocation = [position.coords.latitude, position.coords.longitude];
            setLocation(newLocation);
            setPermissionDenied(false);
          },
          (error) => {
            console.error("Error fetching location:", error);
            if (error.code === error.PERMISSION_DENIED) {
              setPermissionDenied(true);
            }
          }
        );
      } catch (error) {
        console.error("Error checking permissions:", error);
      }
    };

    checkPermissionAndGetLocation();
  }, []);

  // Separate useEffect for timeout based on location update
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!location) {
        setMessage("Location not detected. Please enable GPS and allow location access.");
        setMessageType("error");
      }
    }, 5000);

    return () => clearTimeout(timeout);
  }, [location]);

  useEffect(() => {
    getClockInRecord();
  }, [getClockInRecord])

  const handleLocationCheck = useCallback((inside) => {
    setIsInside(inside);
  }, []);

  const handleClockIn = async () => {
    setIsLoading(true);
    if (!isInside) {
      alert("You must be inside the designated area to clock in.");
      return;
    }

    try {
      await api.post(
        "/clock-record/clock-in",
        { location, remarks },
        { headers: { Authorization: token } }
      );
      setMessage("Clock in successfully");
      setMessageType("success");
      getClockInRecord();
    } catch (err) {
      setMessage(err.response?.data?.error || "Clock in failed.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  const detectMobileOS = () => {
    const userAgent = navigator.userAgent || window.opera;
  
    if (/android/i.test(userAgent)) {
      return "Android";
    }
    if (/iPhone|iPad|iPod/i.test(userAgent)) {
      return "iOS";
    }
    return "Unknown";
  };

  const handleOvertimeSubmit = async (data) => {
    try {
      const completeData = {
        userId: data?.userId,
        remarks: data?.remarks?.trim() ? data.remarks : "OT",
        location: location, 
      };
      await api.post("/clock-record/overtime", completeData, 
        {headers: { Authorization: token }},
      );

      setMessage("Overtime clocked in successfully.");
      setMessageType("success");
      setIsOvertimeModalOpen(false);
      getClockInRecord();
    } catch (error) {
      setMessage(error.response?.data?.error || "Failed to clock in overtime.");
      setMessageType("error");
    }
  };
  
  const handleClockOut = async () => {
    setIsLoading(true);
    if (!isInside) {
      alert("You must be inside the designated area to clock out.");
      return;
    }

    try {
      await api.post(
        "/clock-record/clock-out",
        { location, remarks },
        { headers: { Authorization: token } }
      );
      setMessage("Clock out successfully");
      setMessageType("success");
      getClockInRecord();
    } catch (err) {
      setMessage(err.response?.data?.error || "Clock out failed.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    localStorage.removeItem("accessToken");
    navigate("/login"); // Redirect to login
  };

  const handleUpdateRemarks = async (recordId) => {
    try{
      const response = await api.patch(
        `/clock-record/${recordId}/remarks`,
        { remarks },
        { headers: { Authorization: token } }
      );

      setRemarks(response.data);
      setMessage("Remarks update successfully");
      setMessageType("success");
    }catch(err){
      setMessage(err.response?.data?.error || "Update remarks failed.");
      setMessageType("error");
    }
  }

  return (
    <div className="user-page">
      <h1>Employee Clock-In/Out</h1>
      <button onClick={() => navigate("/leave-request")} className="clock-button">
        Request Leave
      </button>
      <button onClick={handleLogout} className="logout-button">
        Logout
      </button>
      <div className="clock-buttons">
        <button
          onClick={handleClockIn}
          disabled={!isInside || isLoading || clockRecords}
          className="clock-button"
        >
          Clock In
        </button>
        <button
          onClick={handleClockOut}
          disabled={!isInside || isLoading}
          className="clock-button"
        >
          Clock Out
        </button>
        <button 
          disabled={!isInside || isLoading  || clockRecords}
          className="clock-button" 
          onClick={() => {
            setIsOvertimeModalOpen(true); 
            fetchUsers()}}
        >
          Overtime
        </button>

      </div>
      <div className="clock-buttons">
        <input type="text" placeholder="Remarks" className="form-input" value={remarks ?? ""} onChange={e => setRemarks(e.target.value)}/>
        <button className="clock-button" disabled={!clockRecords} onClick={() => handleUpdateRemarks(clockRecords?.id)}>Update</button>
      </div>
      <div className="user-page">
        <div className="user-info">
          <label>Full Name</label>
          <input type="text" value={clockRecords?.fullname ?? "-"} disabled className="input-field"/>
        </div>
        
        <div className="user-info">
          <label>Username</label>
          <input type="text" value={clockRecords?.username ?? "-"} disabled className="input-field"/>
        </div>

        <div className="user-info">
          <label>Clock In Time</label>
          <input 
            type="text" 
            value={clockRecords?.clock_in_time 
              ? format(new Date(clockRecords.clock_in_time), "dd MMMM yyyy hh:mm aa") 
              : "-"} 
            disabled 
            className="input-field" 
          />
        </div>
      </div>
              
    <Modal
      isOpen={isOvertimeModalOpen}
      onClose={() => setIsOvertimeModalOpen(false)}
      title="Overtime Clock In"
      fields={[
        { name: "userId", label: "Cover", type: "dropdown", require: true,
          dropdownOptions: userDropdown.map(user => ({
            id: user.id,
            name: user.fullname,
          }))},
        { name: "remarks", label: "Remarks" },
      ]}
      onSubmit={(data) => handleOvertimeSubmit(data)}
      mode="create"
    />

      {message && (
        <p className={`message ${messageType === "error" ? "error" : ""}`}>
          {message}
        </p>
      )}
      {permissionDenied ? (
        <div className={`${messageType}-message`}>
          <p><strong>Location permission is denied. Please enable location access.</strong></p>
          <p>Follow these steps to enable location services on your device:</p>

          {detectMobileOS() === "Android" && (
            <ol>
              <li>Open <strong>Settings</strong>.</li>
              <li>Go to <strong>Location</strong> and turn it on.</li>
              <li>Open <strong>Chrome</strong> (or your browser).</li>
              <li>Go to <strong>Settings &gt; Site settings &gt; Location</strong>.</li>
              <li>Make sure it is set to <strong>Allowed</strong>.</li>
              <li>Refresh this page and try again.</li>
            </ol>
          )}

          {detectMobileOS() === "iOS" && (
            <ol>
              <li>Open <strong>Settings</strong>.</li>
              <li>Go to <strong>Privacy & Security</strong>.</li>
              <li>Select <strong>Location Services</strong> and turn it on.</li>
              <li>Scroll down to <strong>Safari</strong> (or your browser).</li>
              <li>Set <strong>Location Access</strong> to <strong>While Using the App</strong>.</li>
              <li>Go back to the browser and refresh this page.</li>
            </ol>
          )}

          {detectMobileOS() === "Unknown" && <p>Check your device settings and allow location access.</p>}
        </div>
      ) : location ? (
        <ClockInMap userLocation={location} onLocationCheck={handleLocationCheck} />
      ) : (
        <p>Loading map...</p>
      )}
    </div>
  );
}

export default UserPage;